import { useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Title } from '@mantine/core'
import { useTranslation } from 'react-i18next'

import { SearchBar } from '@percent/workplace-giving/common/components/SearchBar/SearchBar'
import classes from './SearchPageHeader.module.scss'
import { Button, Icon, Text } from '@percent/lemonade'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { usePermission } from '@percent/workplace-giving/common/hooks/usePermission/usePermission'
import { scope } from '@percent/workplace-giving/common/hoc/withPermissions'
import { usePostHogFeatureFlag } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { POSTHOG_FEATURE_FLAGS } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'

type SearchPageHeaderProps = Readonly<{
  query?: string
  countryCode?: string
  onCountryChange?: (country: string) => void
  onQueryChange: (q: string) => void
  breadcrumbsPageName?: string
  searchBarPlaceholder?: string
  emptyQueryPageTitle?: string
  allowEmptyQuery?: boolean
  hideCountrySelector?: boolean
  hideAiPrompt?: boolean
}>

export function SearchPageHeader({
  query,
  onQueryChange,
  countryCode = '',
  onCountryChange,
  breadcrumbsPageName,
  searchBarPlaceholder,
  emptyQueryPageTitle,
  allowEmptyQuery,
  hideCountrySelector,
  hideAiPrompt
}: SearchPageHeaderProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state } = useLocation()
  const borderRef = useRef<HTMLDivElement>(null)
  const has = usePermission()
  const givingAiEnabled = has(scope('giving_ai'))
  const aiSearchEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.WPG_AI_SEARCH)
  const { track } = useAnalytics()
  const showAiPrompt = !hideAiPrompt && aiSearchEnabled && givingAiEnabled

  const handleAskAiClick = () => {
    track({
      event: 'AI Search Clicked',
      properties: { origin: 'Search Page', hasQuery: !!query }
    })
    navigate(AppRoute.AI_SEARCH, { state: query ? { initialMessage: query } : undefined })
  }

  return (
    <div className={classes.HeaderWrapper}>
      {breadcrumbsPageName ? (
        <div className={classes.Breadcrumbs} data-testid="search-page-header-breadcrumbs">
          <button
            type="button"
            onClick={() => navigate(state?.originRoute || `${AppRoute.SEARCH}${query ? `?query=${query}` : ''}`)}
          >
            {state?.originRouteName || t('workplace_giving.globalSearch.searchResults.breadcrumb')}
          </button>
          <Icon name="chevron-right" size={3} color="gray100" />
          <Text size="small">{breadcrumbsPageName}</Text>
        </div>
      ) : null}
      <Title className={classes.Title} order={2}>
        {emptyQueryPageTitle && !query
          ? emptyQueryPageTitle
          : t('workplace_giving.globalSearch.heading', {
              searchTerm: query,
              interpolation: { escapeValue: false }
            })}
      </Title>
      <SearchBar
        query={query}
        onQueryChange={onQueryChange}
        countryCode={countryCode}
        onCountryChange={onCountryChange}
        size="medium"
        placeholder={searchBarPlaceholder}
        allowEmptyQuery={allowEmptyQuery}
        hideCountrySelector={hideCountrySelector}
        disableMultiline
      />
      {showAiPrompt && (
        <div className={classes.AiPromptRow}>
          <span className={classes.AiPromptText}>{t('workplace_giving.search.aiPromptText')}</span>
          <div
            ref={borderRef}
            className={classes.AiBorderWrapper}
            onMouseEnter={() =>
              borderRef.current?.getAnimations?.({ subtree: true })?.forEach(a => {
                a.cancel()
                a.play()
              })
            }
          >
            <Button
              variant="secondary"
              shape="oval"
              size="small"
              startIcon="reward-stars"
              startIconSize={4}
              onClick={handleAskAiClick}
            >
              {t('workplace_giving.common.askAi')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
